import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import { DndContext, useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { FileText, CheckCircle2, Clock, Loader2, RefreshCw, AlertTriangle, Send, Move, X, Edit3, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import SignatureModal from '../components/SignatureModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Font mapping for signature display
const getSignatureFontStyle = (fontName) => {
  switch (fontName) {
    case 'Dancing Script':
      return "'Dancing Script', cursive";
    case 'Pinyon Script':
      return "'Pinyon Script', cursive";
    default:
      return 'Caveat, cursive';
  }
};

// Static (non-draggable) Signature Component - for confirmed signatures
function StaticSignature({ field, scale }) {
  const style = {
    left: `${field.x * scale}px`,
    top: `${field.y * scale}px`,
    width: `${field.width * scale}px`,
    minHeight: `${field.height * scale}px`,
    zIndex: 10,
  };

  return (
    <div
      className="absolute bg-green-50/90 border-2 border-green-400 rounded-lg p-2 shadow-md"
      style={style}
    >
      <div className="flex flex-col items-center justify-center">
        {field.signature_type === 'drawn' && field.signature_data ? (
          <img 
            src={field.signature_data} 
            alt="Unterschrift" 
            className="max-w-full object-contain"
            style={{ 
              maxHeight: `${field.height * scale * 0.7}px`,
              minHeight: '30px'
            }}
          />
        ) : field.signature_type === 'typed' && field.signature_data ? (
          <span 
            className="text-xl font-medium text-zinc-800"
            style={{ fontFamily: getSignatureFontStyle(field.signature_font) }}
          >
            {field.signature_data}
          </span>
        ) : (
          <CheckCircle2 className="h-6 w-6 text-green-600" />
        )}
        {field.signer_name && (
          <span className="text-xs text-zinc-700 mt-1 font-medium">{field.signer_name}</span>
        )}
        {field.signer_role && (
          <span className="text-xs text-zinc-500">{field.signer_role}</span>
        )}
        {field.signed_at && (
          <span className="text-[9px] text-zinc-400 mt-1">
            Unterschrieben via SignFlow am {new Date(field.signed_at).toLocaleDateString('de-DE')}
          </span>
        )}
      </div>
      <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1 shadow">
        <CheckCircle2 className="h-3 w-3" />
      </div>
    </div>
  );
}

// Draggable Signature Component - only for editable (unconfirmed) signatures
function DraggableSignature({ field, scale, isEditable, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: field.id,
    disabled: !isEditable, // Disable dragging if not editable
  });

  const style = {
    left: `${field.x * scale}px`,
    top: `${field.y * scale}px`,
    width: `${field.width * scale}px`,
    minHeight: `${field.height * scale}px`,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    cursor: isEditable ? (isDragging ? 'grabbing' : 'grab') : 'default',
    zIndex: isDragging ? 1000 : 10,
  };

  return (
    <div
      ref={setNodeRef}
      {...(isEditable ? { ...listeners, ...attributes } : {})}
      className={`absolute bg-green-50/90 border-2 rounded-lg p-2 transition-shadow ${
        isEditable ? 'border-amber-400' : 'border-green-400'
      } ${isDragging ? 'shadow-xl' : 'shadow-md hover:shadow-lg'}`}
      style={style}
    >
      <div className="flex flex-col items-center justify-center">
        {field.signature_type === 'drawn' && field.signature_data ? (
          <img 
            src={field.signature_data} 
            alt="Unterschrift" 
            className="max-w-full object-contain"
            style={{ 
              maxHeight: `${field.height * scale * 0.7}px`,
              minHeight: '30px'
            }}
          />
        ) : field.signature_type === 'typed' && field.signature_data ? (
          <span 
            className="text-xl font-medium text-zinc-800"
            style={{ fontFamily: getSignatureFontStyle(field.signature_font) }}
          >
            {field.signature_data}
          </span>
        ) : (
          <CheckCircle2 className="h-6 w-6 text-green-600" />
        )}
        {field.signer_name && (
          <span className="text-xs text-zinc-700 mt-1 font-medium">{field.signer_name}</span>
        )}
        {field.signer_role && (
          <span className="text-xs text-zinc-500">{field.signer_role}</span>
        )}
        {field.signed_at && (
          <span className="text-[9px] text-zinc-400 mt-1">
            Unterschrieben via SignFlow am {new Date(field.signed_at).toLocaleDateString('de-DE')}
          </span>
        )}
      </div>
      {isEditable ? (
        <>
          <div className="absolute -top-2 -right-2 bg-amber-500 text-white rounded-full p-1 shadow">
            <Move className="h-3 w-3" />
          </div>
          <div 
            className="absolute -top-2 -left-2 bg-amber-500 text-white rounded-full p-1.5 shadow cursor-pointer hover:bg-amber-600 z-50"
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onEdit && onEdit(field.id);
            }}
            title="Unterschrift ändern"
          >
            <Edit3 className="h-3 w-3" />
          </div>
        </>
      ) : (
        <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1 shadow">
          <CheckCircle2 className="h-3 w-3" />
        </div>
      )}
    </div>
  );
}

export default function SignDocument() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isArchived, setIsArchived] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [signatureFields, setSignatureFields] = useState([]);
  const [hasPositionChanges, setHasPositionChanges] = useState(false);
  const [savingPosition, setSavingPosition] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [remainingSignatures, setRemainingSignatures] = useState(0);
  const [pageWidth, setPageWidth] = useState(800);
  const [pdfError, setPdfError] = useState(false);
  const [pdfRetryCount, setPdfRetryCount] = useState(0);
  const [pdfUrl, setPdfUrl] = useState(null);
  const signatureFieldRefs = useRef({});
  
  // Track which fields can still be edited (before OK confirmation)
  const [editableFieldIds, setEditableFieldIds] = useState(new Set());
  const [lastSignedFieldId, setLastSignedFieldId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const MAX_RETRIES = 5;

  const downloadCurrentPdf = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/document/${documentId}/download-current`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'dokument_aktuell.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    loadDocument();
    updatePageWidth();
    window.addEventListener('resize', updatePageWidth);
    return () => window.removeEventListener('resize', updatePageWidth);
  }, [documentId]);

  // Generate PDF URL with cache-busting
  useEffect(() => {
    if (document) {
      const timestamp = Date.now();
      setPdfUrl(`${API}/document/${documentId}/pdf?t=${timestamp}&retry=${pdfRetryCount}`);
      setSignatureFields(document.signature_fields || []);
      setPdfError(false);
    }
  }, [document, documentId, pdfRetryCount]);

  // Auto-retry on PDF error
  useEffect(() => {
    if (pdfError && pdfRetryCount < MAX_RETRIES) {
      const timer = setTimeout(() => {
        setPdfRetryCount(prev => prev + 1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [pdfError, pdfRetryCount]);

  // Scroll to first unsigned field when PDF is loaded
  useEffect(() => {
    if (numPages && signatureFields.length > 0) {
      const firstUnsignedField = signatureFields.find(f => !f.signed);
      if (firstUnsignedField) {
        // Wait for PDF pages to render, then scroll
        setTimeout(() => {
          const fieldElement = signatureFieldRefs.current[firstUnsignedField.id];
          if (fieldElement) {
            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 500);
      }
    }
  }, [numPages, signatureFields]);

  const updatePageWidth = () => {
    const isMobile = window.innerWidth < 768;
    const maxWidth = isMobile 
      ? window.innerWidth - 32
      : Math.min(window.innerWidth - 100, 800);
    setPageWidth(maxWidth);
  };

  const loadDocument = async () => {
    try {
      const response = await axios.get(`${API}/document/${documentId}`);
      setDocument(response.data);
      setSignatureFields(response.data.signature_fields || []);
      setLoading(false);
      setPdfError(false);
      setIsArchived(false);
    } catch (error) {
      if (error.response?.status === 410) {
        // Document is archived
        if (error.response?.data?.detail === 'archived') {
          setIsArchived(true);
        } else {
          toast.error('Dieses Dokument ist abgelaufen');
        }
      } else {
        toast.error('Dokument konnte nicht geladen werden');
      }
      setLoading(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, delta } = event;
    if (!delta) return;

    const scale = pageWidth / 595;
    const fieldId = active.id;

    setSignatureFields(prev => prev.map(field => {
      if (field.id === fieldId) {
        return {
          ...field,
          x: Math.max(0, field.x + delta.x / scale),
          y: Math.max(0, field.y + delta.y / scale)
        };
      }
      return field;
    }));
    setHasPositionChanges(true);
  };

  const savePositionChanges = async () => {
    setSavingPosition(true);
    try {
      await axios.put(`${API}/document/${documentId}/fields`, signatureFields);
      setHasPositionChanges(false);
      toast.success('Position gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern der Position');
      console.error(error);
    } finally {
      setSavingPosition(false);
    }
  };

  const handlePdfLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPdfError(false);
    setPdfRetryCount(0);
  }, []);

  const handlePdfLoadError = useCallback((error) => {
    console.error('PDF load error:', error);
    
    if (pdfRetryCount < MAX_RETRIES) {
      // Auto-retry with delay
      setTimeout(() => {
        setPdfRetryCount(prev => prev + 1);
      }, 1500);
    } else {
      setPdfError(true);
    }
  }, [pdfRetryCount]);

  const handleManualRetry = () => {
    setPdfError(false);
    setPdfRetryCount(0);
    // Force re-render with new URL
    setPdfUrl(`${API}/document/${documentId}/pdf?t=${Date.now()}&force=true`);
    toast.info('PDF wird neu geladen...');
  };

  // Get prefilled data for the current signer based on their email (if they opened from email link)
  const getPrefillData = () => {
    if (!document?.recipient_details || document.recipient_details.length === 0) {
      return { name: '', role: '', email: '' };
    }
    // For now, return the first recipient's data (can be enhanced later to match by URL params)
    const firstRecipient = document.recipient_details[0];
    return {
      name: firstRecipient?.name || '',
      role: firstRecipient?.role || '',
      email: firstRecipient?.email || ''
    };
  };

  const handleFieldClick = (field) => {
    // Allow clicking on unsigned fields OR editable signed fields
    if (!field.signed || editableFieldIds.has(field.id)) {
      setSelectedField(field);
      setShowSignatureModal(true);
    }
  };

  const handleEditSignature = (fieldId) => {
    const field = signatureFields.find(f => f.id === fieldId);
    if (field) {
      setSelectedField(field);
      setShowSignatureModal(true);
    }
  };

  const handleConfirmSignature = () => {
    // Remove the field from editable set - signature is now final
    setEditableFieldIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(lastSignedFieldId);
      return newSet;
    });
    setLastSignedFieldId(null);
    setShowSuccessModal(false);
  };

  const handleSignature = async (signatureData, signatureType, email, signerName, signerRole, signatureFont) => {
    try {
      const response = await axios.post(`${API}/document/${documentId}/sign/${selectedField.id}`, {
        signature_data: signatureData,
        signature_type: signatureType,
        signer_email: email,
        signer_name: signerName,
        signer_role: signerRole || null,
        signature_font: signatureFont || null
      });

      await loadDocument();
      setShowSignatureModal(false);
      
      const signedFieldId = selectedField.id;
      setSelectedField(null);

      // Mark this field as editable until confirmed
      setEditableFieldIds(prev => new Set([...prev, signedFieldId]));
      setLastSignedFieldId(signedFieldId);

      // Calculate remaining signatures
      const allSigned = response.data.all_signed;
      const updatedFields = signatureFields.map(f => 
        f.id === signedFieldId ? { ...f, signed: true } : f
      );
      const remaining = updatedFields.filter(f => !f.signed).length;
      setRemainingSignatures(remaining);

      // Scroll to the signed field after a short delay
      setTimeout(() => {
        const fieldElement = signatureFieldRefs.current[signedFieldId];
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);

      // Show success modal
      setShowSuccessModal(true);

    } catch (error) {
      toast.error('Fehler beim Speichern der Unterschrift');
      console.error(error);
    }
  };

  const getTimeRemaining = () => {
    if (!document) return '';
    const expires = new Date(document.expires_at);
    const now = new Date();
    const diff = expires - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    return `${days} Tag${days !== 1 ? 'e' : ''}`;
  };

  const handleSendReminder = async () => {
    setSendingReminder(true);
    try {
      await axios.post(`${API}/document/${documentId}/send-reminder`);
      toast.success('Erinnerung wurde an alle ausstehenden Unterzeichner gesendet!');
    } catch (error) {
      toast.error('Fehler beim Senden der Erinnerung');
      console.error(error);
    } finally {
      setSendingReminder(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-zinc-600" />
          <p className="text-zinc-600">Dokument wird geladen...</p>
        </div>
      </div>
    );
  }

  // Show archived message
  if (isArchived) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="p-8 max-w-lg text-center border-amber-200 bg-amber-50">
          <div className="h-16 w-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold mb-4 text-zinc-900">Dokument archiviert</h2>
          <p className="text-zinc-700 mb-6">
            Dieses Dokument wurde archiviert und ist nicht mehr zur Unterzeichnung verfügbar.
          </p>
          <div className="bg-white border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-zinc-600">
              Bitte wenden Sie sich an Ihren Ansprechpartner, falls Sie weiterhin Zugriff auf dieses Dokument benötigen.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => window.location.href = 'https://www.hvkurz.de'}
              className="bg-zinc-950 text-white hover:bg-zinc-800"
            >
              Zur Hauptseite
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = 'mailto:info@hvkurz.de'}
            >
              Kontakt aufnehmen
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold mb-4">Dokument nicht gefunden</h2>
          <p className="text-zinc-600">Das angeforderte Dokument existiert nicht oder ist abgelaufen.</p>
        </Card>
      </div>
    );
  }

  const unsignedFields = signatureFields.filter(f => !f.signed);
  const signedFields = signatureFields.filter(f => f.signed);

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 sm:h-10 sm:w-10 bg-zinc-950 rounded-md flex items-center justify-center">
                <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h1 className="text-base sm:text-xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {document.original_filename}
                </h1>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-zinc-500">
                  <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Gültig für weitere {getTimeRemaining()}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto justify-between sm:justify-end">
              <div className="text-right">
                <div className="text-xs text-zinc-500 uppercase tracking-wide">Dokument-ID</div>
                <div className="text-xs sm:text-sm font-mono font-medium text-zinc-700" data-testid="document-id">
                  {documentId.slice(0, 8)}
                </div>
              </div>
              <div className="h-8 w-px bg-zinc-200 hidden sm:block"></div>
              <div className="text-right hidden sm:block">
                <div className="text-sm font-medium text-zinc-700">
                  {signedFields.length} von {signatureFields.length} unterschrieben
                </div>
              </div>
              <div className="h-8 w-px bg-zinc-200 hidden sm:block"></div>
              <Button
                data-testid="download-current-pdf"
                variant="outline"
                size="sm"
                onClick={downloadCurrentPdf}
                disabled={downloading}
                className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-4 gap-2"
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Speichern & Download</span>
                <span className="sm:hidden">PDF</span>
              </Button>
              <Button
                data-testid="back-to-hvkurz-button"
                variant="outline"
                size="sm"
                onClick={() => window.open('https://www.hvkurz.de', '_blank')}
                className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-4"
              >
                <span className="hidden sm:inline">zurück zur Verwaltung</span>
                <span className="sm:hidden">Verwaltung</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        {unsignedFields.length > 0 && (
          <Card className="p-6 mb-8 border-orange-300 bg-orange-50/50" data-testid="signature-instructions">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">Unterschrift erforderlich</h3>
                <p className="text-zinc-700">
                  Bitte klicken Sie auf die markierten orangefarbenen Felder im Dokument, um Ihre Unterschrift hinzuzufügen.
                </p>
                {signedFields.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-orange-200">
                    <p className="text-sm text-zinc-600 mb-3">
                      Sie haben bereits {signedFields.length} von {document.signature_fields.length} Feldern unterschrieben.
                      {unsignedFields.length > 0 && ' Möchten Sie die anderen Unterzeichner erinnern?'}
                    </p>
                    <Button
                      data-testid="send-reminder-button"
                      onClick={handleSendReminder}
                      disabled={sendingReminder}
                      variant="outline"
                      className="gap-2 border-orange-400 text-orange-700 hover:bg-orange-100"
                    >
                      {sendingReminder ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Dokument weitersenden
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Position Save Button */}
        {hasPositionChanges && (
          <Card className="p-4 mb-4 border-blue-300 bg-blue-50">
            <div className="flex items-center justify-between">
              <p className="text-sm text-blue-800">
                <Move className="h-4 w-4 inline mr-2" />
                Sie haben die Position Ihrer Unterschrift geändert.
              </p>
              <Button
                data-testid="save-position-button"
                onClick={savePositionChanges}
                disabled={savingPosition}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {savingPosition ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Speichern...
                  </>
                ) : (
                  'Position speichern'
                )}
              </Button>
            </div>
          </Card>
        )}

        {/* PDF Viewer with Action Panel */}
        <div className="flex flex-col lg:flex-row gap-6 justify-center items-start">
          {/* PDF Container */}
          <DndContext onDragEnd={handleDragEnd}>
            <div className="pdf-container bg-white shadow-2xl shadow-zinc-900/10 rounded-sm" data-testid="pdf-viewer">
              {pdfError ? (
                <div className="flex flex-col items-center justify-center p-12 min-h-[400px]">
                  <AlertTriangle className="h-16 w-16 text-orange-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">PDF konnte nicht geladen werden</h3>
                  <p className="text-zinc-600 mb-4 text-center max-w-md">
                    Die PDF-Datei ist nicht verfügbar. Dies kann passieren, wenn das Dokument nach einem System-Update neu hochgeladen werden muss.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleManualRetry}
                      variant="outline"
                      className="gap-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Neu laden
                    </Button>
                    <Button
                      onClick={() => window.location.href = '/'}
                      className="gap-2 bg-zinc-950 text-white hover:bg-zinc-800"
                    >
                      Zur Startseite
                    </Button>
                  </div>
                </div>
              ) : (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={handlePdfLoadSuccess}
                  onLoadError={handlePdfLoadError}
                  loading={
                    <div className="flex flex-col items-center justify-center p-12 min-h-[400px]">
                      <Loader2 className="h-8 w-8 animate-spin text-zinc-600 mb-4" />
                      <p className="text-zinc-600">
                        {pdfRetryCount > 0 
                          ? `PDF wird geladen... (Versuch ${pdfRetryCount + 1}/${MAX_RETRIES + 1})`
                          : 'PDF wird geladen...'}
                      </p>
                    </div>
                  }
                  error={
                    <div className="flex flex-col items-center justify-center p-12 min-h-[400px]">
                      <AlertTriangle className="h-12 w-12 text-orange-500 mb-4" />
                      <p className="text-zinc-600 mb-4">Laden fehlgeschlagen</p>
                      <Button
                        onClick={handleManualRetry}
                        variant="outline"
                        className="gap-2"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Neu laden
                      </Button>
                    </div>
                  }
                >
                  {Array.from(new Array(numPages), (el, index) => (
                    <div key={`page_${index + 1}`} className="relative mb-4" data-testid={`pdf-page-${index}`}>
                      <Page
                        pageNumber={index + 1}
                        width={pageWidth}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                      />
                      {/* Render signature fields for this page */}
                      {signatureFields
                        .filter(field => field.page === index)
                        .map(field => {
                          const scale = pageWidth / 595;
                          
                          // Show signature for signed fields
                          if (field.signed) {
                            const isEditable = editableFieldIds.has(field.id);
                            
                            // Use static component for confirmed signatures (not editable)
                            // Use draggable component only for unconfirmed signatures
                            return (
                              <div
                                key={field.id}
                                ref={el => signatureFieldRefs.current[field.id] = el}
                              >
                                {isEditable ? (
                                  <DraggableSignature
                                    field={field}
                                    scale={scale}
                                    isEditable={true}
                                    onEdit={handleEditSignature}
                                  />
                                ) : (
                                  <StaticSignature
                                    field={field}
                                    scale={scale}
                                  />
                                )}
                              </div>
                            );
                          }
                          
                          // Show clickable placeholder for unsigned fields
                          return (
                            <div
                              key={field.id}
                              ref={el => signatureFieldRefs.current[field.id] = el}
                              data-testid={`signature-field-${field.id}`}
                              className={`signature-field ${selectedField?.id === field.id ? 'active' : ''}`}
                              style={{
                                left: `${field.x * scale}px`,
                                top: `${field.y * scale}px`,
                                width: `${field.width * scale}px`,
                                height: `${field.height * scale}px`
                              }}
                              onClick={() => handleFieldClick(field)}
                            >
                              <span>Hier unterschreiben</span>
                            </div>
                          );
                        })}
                    </div>
                  ))}
                </Document>
              )}
            </div>
          </DndContext>

          {/* Action Panel - Right Side */}
          {unsignedFields.length > 0 && (
            <div className="hidden lg:block w-80 sticky top-24">
              <Card className="p-6 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
                <div className="text-center space-y-4">
                  <div className="h-16 w-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                    <FileText className="h-8 w-8 text-orange-600" />
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-zinc-900 mb-2">
                      Ihre Unterschrift wird benötigt
                    </h3>
                    <p className="text-sm text-zinc-600">
                      Klicken Sie auf den Button unten oder auf eines der markierten Felder im Dokument.
                    </p>
                  </div>

                  <div className="pt-2">
                    <Button
                      data-testid="sign-now-button"
                      onClick={() => {
                        const firstUnsigned = signatureFields.find(f => !f.signed);
                        if (firstUnsigned) {
                          setSelectedField(firstUnsigned);
                          setShowSignatureModal(true);
                        }
                      }}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white h-12 text-base font-semibold gap-2"
                    >
                      <FileText className="h-5 w-5" />
                      Jetzt unterschreiben
                    </Button>
                  </div>

                  <div className="pt-4 border-t border-orange-200">
                    <div className="flex items-center justify-center gap-2 text-sm text-zinc-600">
                      <div className="flex items-center gap-1">
                        <div className="h-3 w-3 rounded-full bg-green-500"></div>
                        <span>{signedFields.length} erledigt</span>
                      </div>
                      <span>•</span>
                      <div className="flex items-center gap-1">
                        <div className="h-3 w-3 rounded-full bg-orange-500"></div>
                        <span>{unsignedFields.length} offen</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Hint */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  <strong>Tipp:</strong> Sie können auch direkt auf die orangefarbenen Felder im Dokument klicken, um zu unterschreiben.
                </p>
              </div>

              {/* Editable signature hint */}
              {editableFieldIds.size > 0 && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-800 mb-2">
                    <strong>Ihre Unterschrift kann noch geändert werden.</strong>
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const editableId = Array.from(editableFieldIds)[0];
                      handleEditSignature(editableId);
                    }}
                    className="w-full text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                  >
                    Unterschrift ändern
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* All signed panel */}
          {unsignedFields.length === 0 && signedFields.length > 0 && (
            <div className="hidden lg:block w-80 sticky top-24">
              <Card className="p-6 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                <div className="text-center space-y-4">
                  <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-zinc-900 mb-2">
                      Alle Unterschriften erledigt
                    </h3>
                    <p className="text-sm text-zinc-600">
                      Dieses Dokument wurde vollständig unterschrieben.
                    </p>
                  </div>

                  <div className="pt-4 border-t border-green-200">
                    <p className="text-sm text-green-700">
                      ✓ {signedFields.length} von {signatureFields.length} Unterschriften
                    </p>
                  </div>

                  {/* Editable signature option for completed documents */}
                  {editableFieldIds.size > 0 && (
                    <div className="pt-4 border-t border-green-200">
                      <p className="text-xs text-amber-700 mb-2">
                        Sie können Ihre Unterschrift noch ändern:
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const editableId = Array.from(editableFieldIds)[0];
                          handleEditSignature(editableId);
                        }}
                        className="w-full text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                      >
                        Unterschrift ändern
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      {showSignatureModal && (
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => {
            setShowSignatureModal(false);
            setSelectedField(null);
          }}
          onSave={handleSignature}
          isBeschlussprotokoll={document?.is_beschlussprotokoll || false}
          prefillName={getPrefillData().name}
          prefillRole={getPrefillData().role}
          prefillEmail={getPrefillData().email}
        />
      )}

      {/* Success Modal after signing */}
      <Dialog open={showSuccessModal} onOpenChange={(open) => {
        if (!open) handleConfirmSignature();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              Unterschrift gespeichert!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            <p className="text-zinc-600">
              Ihre Unterschrift wurde erfolgreich gespeichert.
            </p>
            
            {remainingSignatures > 0 ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Hinweis:</strong> Es {remainingSignatures === 1 ? 'wird noch eine weitere Unterschrift' : `werden noch ${remainingSignatures} weitere Unterschriften`} benötigt. 
                  Sobald alle Parteien unterschrieben haben, erhalten Sie das fertige Dokument per E-Mail zugeschickt.
                </p>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 text-sm">
                  <strong>Fertig!</strong> Alle Unterschriften wurden gesammelt. Das unterschriebene Dokument wird nun per E-Mail an den Absender gesendet.
                </p>
              </div>
            )}
            
            <p className="text-zinc-500 text-sm">
              Sie können diese Seite jetzt schließen.
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  const fieldIdToEdit = lastSignedFieldId;
                  setShowSuccessModal(false);
                  // Small delay to ensure modal is closed before opening new one
                  setTimeout(() => {
                    if (fieldIdToEdit) {
                      handleEditSignature(fieldIdToEdit);
                    }
                  }, 100);
                }}
                className="flex-1"
              >
                Unterschrift ändern
              </Button>
              <Button
                onClick={handleConfirmSignature}
                className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800"
              >
                OK
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white mt-24">
        <div className="container mx-auto px-6 py-8">
          <div className="text-center text-sm text-zinc-500 space-y-2">
            <p>Entwickelt von Scholze-Kurz & Kurz Immobilien GmbH</p>
            <div className="flex items-center justify-center gap-4">
              <a
                href="https://www.hvkurz.de/impressum"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-600 hover:text-zinc-900 transition-colors"
              >
                Impressum
              </a>
              <span>|</span>
              <a
                href="https://www.hvkurz.de/datenschutzerklaerung/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-600 hover:text-zinc-900 transition-colors"
              >
                Datenschutz
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}